export type MentionedChannel = {
  name?: string | null
  displayName?: string | null
}

export type MentionedChannelTextPart = {
  key: string
  text: string
  isMentionedChannel: boolean
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Builds a matcher for the channel's own name (`name` is the handle,
 * `displayName` the human name), with or without a leading `@`. An `@handle`
 * may carry a Mastodon-style `@instance` suffix. Boundaries stop partial
 * matches such as `@bufferapp` or `buffer.bsky.social` for `buffer`.
 */
const buildMentionedChannelPattern = (
  channel: MentionedChannel,
): RegExp | null => {
  const candidates = [channel.name, channel.displayName]
    .map((value) => value?.trim().replace(/^@/, '') ?? '')
    .filter((value) => value.length > 0)
    .filter(
      (value, index, all) =>
        all.findIndex(
          (other) => other.toLowerCase() === value.toLowerCase(),
        ) === index,
    )
    // Longest first so "Buffer Team" wins over "Buffer".
    .toSorted((a, b) => b.length - a.length)
    .map(escapeRegExp)

  if (candidates.length === 0) {
    return null
  }

  const names = candidates.join('|')
  const word = '[\\p{L}\\p{N}_]'
  return new RegExp(
    `(?<!${word}|@)(?:@(?:${names})(?:@[\\w-]+(?:\\.[\\w-]+)+)?|(?:${names}))(?!${word}|-|\\.${word})`,
    'giu',
  )
}

/**
 * Splits text into plain parts and parts naming the mentioned channel, so
 * only the channel's own name is highlighted, not every `@handle`.
 */
export function getMentionedChannelTextParts(
  text: string,
  channel: MentionedChannel,
): MentionedChannelTextPart[] {
  const pattern = buildMentionedChannelPattern(channel)
  if (!pattern) {
    return [{ key: `0:${text}`, text, isMentionedChannel: false }]
  }

  const parts: MentionedChannelTextPart[] = []
  let offset = 0
  for (const match of text.matchAll(pattern)) {
    const start = match.index
    if (start > offset) {
      const plain = text.slice(offset, start)
      parts.push({
        key: `${offset}:${plain}`,
        text: plain,
        isMentionedChannel: false,
      })
    }
    parts.push({
      key: `${start}:${match[0]}`,
      text: match[0],
      isMentionedChannel: true,
    })
    offset = start + match[0].length
  }
  if (offset < text.length || parts.length === 0) {
    const rest = text.slice(offset)
    parts.push({
      key: `${offset}:${rest}`,
      text: rest,
      isMentionedChannel: false,
    })
  }

  return parts
}

/** Whether the whole text names the channel, e.g. a linkified `@handle`. */
export const isMentionedChannelText = (
  text: string,
  channel: MentionedChannel,
): boolean => {
  const parts = getMentionedChannelTextParts(text, channel)
  return parts.length === 1 && parts[0].isMentionedChannel
}
